import React, { useState, useEffect, useMemo } from 'react';
import { db, auth, getCollectionName } from './firebase';
import { collection, query, where, onSnapshot, addDoc, doc, updateDoc, deleteDoc, orderBy, getDoc, resumo } from './firestore-compat';
import { toTitleCase } from './utils/formatters';

const TURNOS = ['07:00 - 19:00', '19:00 - 07:00'];
const DIAS_SEMANA = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

// Retorna { ativa, diasRestantes } se profissional tiver liberação temporária pendente, null caso contrário
const getLiberacaoStatus = (prof) => {
  if (!prof?.liberacaoTemporaria) return null;
  if (prof.statusRH === 'Aprovado' && prof.statusTI === 'Criado') return null;
  const grantedAt = new Date(prof.liberacaoTemporariaEm);
  if (isNaN(grantedAt.getTime())) return { ativa: true, diasRestantes: 30 };
  const expiresAt = new Date(grantedAt.getTime() + 30 * 24 * 60 * 60 * 1000);
  const now = new Date();
  const diasRestantes = Math.ceil((expiresAt - now) / 86400000);
  return { ativa: now < expiresAt, diasRestantes, expiresAt };
};

export default function EscalasTecnicos() {
  const currentUser = auth.currentUser;
  const isPainel = currentUser?.email?.toLowerCase().includes('painel');
  
  // Date Navigation State (Starts on Sunday)
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });

  // Data States
  const [pacientes, setPacientes] = useState([]);
  const [escalas, setEscalas] = useState([]);
  const [profissionaisAtivos, setProfissionaisAtivos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dbError, setDbError] = useState(null);

  // UI States
  const [activeTab, setActiveTab] = useState('vinculados'); // 'vinculados' | 'sem_vinculo'
  const [viewMode, setViewMode] = useState('week'); // 'week' | 'month'
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null); // { paciente, day, turno, existing }
  const [formTecnico, setFormTecnico] = useState('');
  const [isCoringa, setIsCoringa] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [userRole, setUserRole] = useState(null);
  const [userEquipe, setUserEquipe] = useState('');


  // Paciente Form States
  const [isPacienteModalOpen, setIsPacienteModalOpen] = useState(false);
  const [pacienteForm, setPacienteForm] = useState({ nome: '', dataNascimento: '', tipo: 'HB', cooperativas: [], escala: '24h' });
  const [editingPacienteId, setEditingPacienteId] = useState(null);
  const [listaCooperativas, setListaCooperativas] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterFilial, setFilterFilial] = useState('TODOS'); // 'TODOS' | 'HB' | 'HS'
  const [filterCooperativa, setFilterCooperativa] = useState('TODAS');
  const [pacientesCapImplantados, setPacientesCapImplantados] = useState([]);
  const [showCapImplantados, setShowCapImplantados] = useState(false);

  // Fetch Current User Role and Equipe
  useEffect(() => {
    if (!currentUser) {
      // Auth ainda carregando — aguarda um pouco antes de desistir
      const t = setTimeout(() => setLoading(false), 3000);
      return () => clearTimeout(t);
    }
    const userEmail = currentUser.email?.toLowerCase();
    const unsubUser = onSnapshot(query(collection(db, getCollectionName('users')), where('email', '==', userEmail)), snap => {
      if (!snap.empty) {
        const data = snap.docs[0].data();
        const roles = Array.isArray(data.roles) ? data.roles : (data.role ? [data.role] : []);
        const role = roles.includes('admin') || roles.includes('administrador') ? 'admin' : (roles.includes('escalista') ? 'escalista' : (roles.includes('cooperativa') ? 'cooperativa' : 'leitor'));
        setUserRole(role);
        setUserEquipe(data.equipe || '');
      } else {
        // Usuário autenticado mas não encontrado na tabela users — usa admin como fallback
        const isKnownAdmin = currentUser.email === 'alexandrebareaoliveira@gmail.com';
        setUserRole(isKnownAdmin ? 'admin' : 'leitor');
      }
    }, err => {
      console.error('[EscalasTecnicos] Erro ao buscar role:', err);
      setDbError('Erro ao carregar dados do usuário: ' + err.message);
      setUserRole('leitor');
    });
    return () => unsubUser();
  }, [currentUser]);

  // Fetch Pacientes e Profissionais
  useEffect(() => {
    const unsubPacientes = onSnapshot(query(collection(db, getCollectionName('escalas_pacientes')), orderBy('nome')), snap => {
      setPacientes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    const unsubCooperativas = onSnapshot(query(collection(db, getCollectionName('cooperativas_config')), orderBy('name')), snap => {
      setListaCooperativas(snap.docs.map(d => d.data().name));
    }, err => {
      console.error("DEBUG: Erro ao buscar cooperativas:", err);
    });

    // Fetch Profissionais (Puxa ativos e pendentes para permitir escala antecipada)
    const unsubProfissionais = onSnapshot(collection(db, getCollectionName('profissionais_parceiros')), snap => {
      const profs = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(p => p.statusRH !== 'Reprovado' && p.statusRH !== 'Bloqueado' && p.tipo === 'TÉCNICO DE ENFERMAGEM');
      profs.sort((a, b) => a.nome.localeCompare(b.nome));
      setProfissionaisAtivos(profs);
    });

    const unsubCapImplantados = onSnapshot(
      query(collection(db, getCollectionName('captacao_pacientes')), where('status', '==', 'Implantado'), resumo('captacao_resumo')),
      snap => { setPacientesCapImplantados(snap.docs.map(d => ({ id: d.id, ...d.data() }))); },
      () => {}
    );

    return () => { unsubPacientes(); unsubCooperativas(); unsubProfissionais(); unsubCapImplantados(); };
  }, []);

  // Fetch Escalas for current view period (onSnapshot com polling garante sync cross-device)
  useEffect(() => {
    // Usa dataStr (YYYY-MM-DD, horário local) para evitar problemas de fuso com toISOString()
    const pad = n => String(n).padStart(2, '0');
    const toDateStr = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;

    const startStr = toDateStr(currentWeekStart);

    const endDate = new Date(currentWeekStart);
    if (viewMode === 'week') {
      endDate.setDate(endDate.getDate() + 6);
    } else {
      endDate.setMonth(endDate.getMonth() + 1);
      endDate.setDate(0);
    }
    const endStr = toDateStr(endDate);

    const q = query(
      collection(db, getCollectionName('escalas_homecare')),
      where('dataStr', '>=', startStr),
      where('dataStr', '<=', endStr)
    );

    setEscalas([]); // limpa escalas da semana anterior enquanto carrega a nova

    const unsub = onSnapshot(q, snapshot => {
      const rows = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      console.log('[EscalasTecnicos] escalas sincronizadas:', rows.length, 'registros para', startStr, '→', endStr);
      setEscalas(rows);
      setLoading(false);
    }, err => {
      console.error('[EscalasTecnicos] Erro ao buscar escalas:', err);
      setDbError('Erro ao carregar escalas: ' + err.message);
      setLoading(false);
    });

    return unsub;
  }, [currentWeekStart, viewMode]);

  const changePeriod = (offset) => {
    const newDate = new Date(currentWeekStart);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (offset * 7));
    } else {
      newDate.setMonth(newDate.getMonth() + offset);
      newDate.setDate(1);
    }
    setCurrentWeekStart(newDate);
  };

  const goToToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    setCurrentWeekStart(d);
    setViewMode('week');
  };

  const toggleViewMode = () => {
    const newMode = viewMode === 'week' ? 'month' : 'week';
    setViewMode(newMode);
    if (newMode === 'month') {
      const d = new Date(currentWeekStart);
      d.setDate(1);
      setCurrentWeekStart(d);
    } else {
      // Retorna para o dia atual (Hoje) de forma dinâmica
      const d = new Date();
      d.setHours(0,0,0,0);
      setCurrentWeekStart(d);
    }
  };

  const formatHeaderDate = (dateObj) => {
    const d = dateObj.getDate().toString().padStart(2, '0');
    const m = (dateObj.getMonth() + 1).toString().padStart(2, '0');
    return `${d}/${m}`;
  };

  const displayDays = useMemo(() => {
    const days = [];
    const count = viewMode === 'week' ? 7 : new Date(currentWeekStart.getFullYear(), currentWeekStart.getMonth() + 1, 0).getDate();
    
    for (let i = 0; i < count; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(currentWeekStart.getDate() + i);
      days.push({ 
        nome: DIAS_SEMANA[date.getDay()], 
        date, 
        dateStr: date.toISOString().split('T')[0] 
      });
    }
    return days;
  }, [currentWeekStart, viewMode]);

  // Handlers
  const handleAddPaciente = () => {
    setPacienteForm({ nome: '', dataNascimento: '', tipo: 'HB', cooperativas: [], escala: '24h' });
    setEditingPacienteId(null);
    setIsPacienteModalOpen(true);
  };

  const handleEditPaciente = (p) => {
    setPacienteForm({ 
      nome: p.nome, 
      dataNascimento: p.dataNascimento || '', 
      tipo: p.tipo || 'HB',
      cooperativas: p.cooperativas || [],
      escala: p.escala || '24h'
    });
    setEditingPacienteId(p.id);
    setIsPacienteModalOpen(true);
  };
  const handleToggleCoopVinculo = (coop) => {
    setPacienteForm(prev => {
      const coops = prev.cooperativas || [];
      if (coops.includes(coop)) {
        return { ...prev, cooperativas: coops.filter(c => c !== coop) };
      } else {
        return { ...prev, cooperativas: [...coops, coop] };
      }
    });
  };

  const handleSavePaciente = async (e) => {
    e.preventDefault();
    if (!pacienteForm.nome) return alert("Nome é obrigatório");
    
    try {
      const formattedForm = { ...pacienteForm, nome: toTitleCase(pacienteForm.nome) };
      if (editingPacienteId) {
        // UPDATE
        await updateDoc(doc(db, getCollectionName('escalas_pacientes'), editingPacienteId), formattedForm);
      } else {
        // CREATE
        await addDoc(collection(db, getCollectionName('escalas_pacientes')), { 
          ...formattedForm, 
          criadoEm: new Date().toISOString() 
        });
      }
      setIsPacienteModalOpen(false);
      setEditingPacienteId(null);
    } catch (err) {
      console.error(err);
      alert("Erro ao salvar paciente.");
    }
  };

  const handleDeletePaciente = async (id) => {
    if(window.confirm('Deseja excluir este paciente da escala?')) {
      await deleteDoc(doc(db, getCollectionName('escalas_pacientes'), id));
    }
  }

  const handleOpenSlot = (paciente, day, turno) => {
    const existing = escalas.find(e => e.pacienteId === paciente.id && e.dataStr === day.dateStr && e.turno === turno);
    
    // VERIFICAÇÃO RBAC COOPERATIVA: Bloquear abertura de slot que não pertence à sua equipe
    if (userRole === 'cooperativa') {
      const hasTecnico = existing && existing.tecnico;
      const isMyCoop = existing && existing.cooperativa === userEquipe;
      
      if (hasTecnico && !isMyCoop) {
        alert('Acesso Negado: Este turno já foi preenchido por outra cooperativa ou pela central.');
        return;
      }
    }
    
    setSelectedSlot({ paciente, day, turno, existing });
    const existingName = existing ? existing.tecnico : '';
    const existingCoop = existing ? existing.cooperativa : '';
    setFormTecnico(existingName && existingCoop ? `${existingName} - ${existingCoop}` : existingName || '');
    setIsValidated(existing ? !!existing.validadoEscalista : false);
    setSaveError('');
    setIsModalOpen(true);
  };

  const handleSaveSlot = async (e) => {
    e.preventDefault();

    const matchFormTecnico = (p) => p.nome === formTecnico || (p.cooperativa && `${p.nome} - ${p.cooperativa}` === formTecnico);
    let finalTecnicoName = formTecnico;

    // Bloquear alocação em datas futuras se liberação temporária estiver vencida
    if (formTecnico.trim()) {
      const profCheck = profissionaisAtivos.find(matchFormTecnico);
      if (profCheck) {
        finalTecnicoName = profCheck.nome;
      }
      const libCheck = getLiberacaoStatus(profCheck);
      if (libCheck && !libCheck.ativa) {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const slotDate = new Date(selectedSlot.day.dateStr + 'T00:00:00');
        if (slotDate > today) {
          alert(`⚠️ A liberação temporária de ${formTecnico} está vencida há ${Math.abs(libCheck.diasRestantes)} dia(s).\nNão é possível alocar em datas futuras até a documentação ser regularizada.`);
          return;
        }
      }
    }

    setSaving(true);
    console.log('[EscalasTecnicos] salvando — formTecnico:', JSON.stringify(formTecnico), '| existing:', selectedSlot?.existing?.id || 'novo');

    const profSelecionado = profissionaisAtivos.find(matchFormTecnico);
    const coringaDefinido = profSelecionado ? (profSelecionado.tipo === 'Coringa' || profSelecionado.isCoringa === true) : false;
    const cooperativaDefinida = profSelecionado ? profSelecionado.cooperativa : (userRole === 'cooperativa' ? userEquipe : '');
    
    try {
      // Bloqueio extra no salvamento para garantir que ninguém contorne a UI
      if (userRole === 'cooperativa' && selectedSlot.existing) {
        if (selectedSlot.existing.tecnico && selectedSlot.existing.cooperativa !== userEquipe) {
          throw new Error('Acesso negado: Você não pode alterar escalas de terceiros.');
        }
      }
      if (selectedSlot.existing) {
        if (!formTecnico.trim()) {
          await deleteDoc(doc(db, getCollectionName('escalas_homecare'), selectedSlot.existing.id));
        } else {
          await updateDoc(doc(db, getCollectionName('escalas_homecare'), selectedSlot.existing.id), {
            tecnico: finalTecnicoName,
            isCoringa: coringaDefinido,
            cooperativa: cooperativaDefinida,
            validadoEscalista: isValidated
          });
        }
      } else {
        if (formTecnico.trim()) {
          const payload = {
            pacienteId: selectedSlot.paciente.id,
            turno: selectedSlot.turno,
            dataStr: selectedSlot.day.dateStr,
            dataObj: selectedSlot.day.date.toISOString(),
            tecnico: finalTecnicoName,
            isCoringa: coringaDefinido,
            cooperativa: cooperativaDefinida,
            validadoEscalista: isValidated,
            criadoPor: currentUser?.email || 'unknown',
            criadoEm: new Date().toISOString()
          };
          const ref = await addDoc(collection(db, getCollectionName('escalas_homecare')), payload);
          console.log('[EscalasTecnicos] addDoc OK — id salvo:', ref.id, '| tecnico:', finalTecnicoName);
        }
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error("Erro ao salvar escala:", error);
      setSaveError(error.message || String(error));
    } finally {
      setSaving(false);
    }
  };

  // Sorting and Filtering Logic: Priority to patients with Open Shifts Today or Tomorrow
  const sortedPacientes = useMemo(() => {
    const today = displayDays[0];
    const getScore = (pId) => {
      let score = 0;
      const pacienteInfo = pacientes.find(p => p.id === pId);
      const turnosDoPaciente = pacienteInfo?.escala === '12h' ? [TURNOS[0]] : 
                               pacienteInfo?.escala === '12h_noite' ? [TURNOS[1]] : 
                               TURNOS;
      turnosDoPaciente.forEach(t => {
        const hasShift = escalas.find(e => e.pacienteId === pId && e.dataStr === today.dateStr && e.turno === t);
        if (!hasShift || !hasShift.tecnico) {
          score += 100; // Prioridade máxima: Escala em aberto HOJE
        } else if (hasShift && hasShift.tecnico) {
          score += 10; // Prioridade média: Em preenchimento
        }
      });
      return score;
    };

    let filtered = pacientes.filter(p => p.status === 'Ativo' || !p.status);

    // Filtro de Filial
    if (filterFilial !== 'TODOS') {
      filtered = filtered.filter(p => p.tipo === filterFilial);
    }

    // Filtro de Busca por Nome
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(p => p.nome.toLowerCase().includes(q));
    }

    // Filtro manual de cooperativa (admin/escalista/painel)
    if (filterCooperativa !== 'TODAS' && (userRole === 'admin' || userRole === 'escalista' || isPainel)) {
      filtered = filtered.filter(p => (Array.isArray(p.cooperativas) ? p.cooperativas : []).includes(filterCooperativa));
    }

    // Filtro por Cooperativa: Só vê pacientes vinculados
    if (userRole === 'cooperativa' && userEquipe) {
      const myEquipe = userEquipe.trim().toUpperCase();
      filtered = filtered.filter(p => {
        const vinculadas = Array.isArray(p.cooperativas) ? p.cooperativas.map(c => c.trim().toUpperCase()) : [];
        return vinculadas.includes(myEquipe);
      });
    } else if (userRole === 'admin' || userRole === 'escalista' || isPainel) {
        // Filtro por Abas para ADM/Escalista/Painel
        filtered = filtered.filter(p => {
            const hasVinculo = Array.isArray(p.cooperativas) && p.cooperativas.length > 0;
            return activeTab === 'vinculados' ? hasVinculo : !hasVinculo;
        });
    }

    const arr = filtered.map(p => ({ ...p, score: getScore(p.id) }));
    arr.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.nome.localeCompare(b.nome);
    });

    return arr;
  }, [pacientes, escalas, displayDays, activeTab, userRole, userEquipe, searchTerm, filterFilial, filterCooperativa]);

  // Day Totals for the Header
  const dayTotals = useMemo(() => {
    return displayDays.map(day => {
      let open = 0;
      let closed = 0;
      sortedPacientes.forEach(p => {
        const turnosDoPaciente = p.escala === '12h' ? [TURNOS[0]] : 
                                 p.escala === '12h_noite' ? [TURNOS[1]] : 
                                 TURNOS;
        turnosDoPaciente.forEach(t => {
          const shift = escalas.find(e => e.pacienteId === p.id && e.dataStr === day.dateStr && e.turno === t);
          if (shift && shift.tecnico) {
            closed++;
          } else {
            open++;
          }
        });
      });
      return { open, closed };
    });
  }, [displayDays, sortedPacientes, escalas]);

  // Styles
  const s = {
    page: { padding: '20px', fontFamily: "'Inter', sans-serif", background: '#f8fafc', minHeight: '100vh', color: '#1e293b', width: '100%', boxSizing: 'border-box' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', background: '#fff', padding: '16px 24px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' },
    title: { margin: 0, fontSize: '1.6rem', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '12px' },
    legend: { display: 'flex', flexWrap: 'wrap', gap: '20px', background: '#fff', padding: '12px 24px', borderRadius: '12px', marginBottom: '20px', fontSize: '0.85rem', fontWeight: 600, boxShadow: '0 2px 4px rgba(0,0,0,0.02)', border: '1px solid #e2e8f0' },
    legendItem: () => ({ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b' }),
    legendDot: (color) => ({ width: '14px', height: '14px', borderRadius: '4px', background: color, boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.1)' }),
    tabs: { display: 'flex', gap: '8px', marginBottom: '16px' },
    tab: (active) => ({ padding: '10px 20px', borderRadius: '10px', background: active ? '#0f172a' : '#fff', color: active ? '#fff' : '#64748b', fontWeight: 700, cursor: 'pointer', border: active ? 'none' : '1px solid #e2e8f0', transition: 'all 0.2s', fontSize: '0.85rem' }),
    controls: { display: 'flex', alignItems: 'center', gap: '12px' },
    btnIcon: { background: '#fff', border: '1px solid #cbd5e1', padding: '10px 16px', borderRadius: '10px', cursor: 'pointer', fontWeight: 700, transition: 'all 0.2s', color: '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    gridContainer: { background: '#fff', borderRadius: '16px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', padding: '20px', width: '100%', boxSizing: 'border-box', border: '1px solid #e2e8f0', overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'separate', borderSpacing: '6px', tableLayout: 'fixed' },
    th: { background: '#475569', color: '#fff', padding: '12px 8px', borderRadius: '8px', textAlign: 'center', fontWeight: 600, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.025em' },
    pacienteCell: (tipo) => ({ 
      background: tipo === 'HB' ? '#fdf2f8' : tipo === 'HS' ? '#eff6ff' : '#1e293b', 
      color: tipo === 'HB' ? '#be185d' : tipo === 'HS' ? '#1d4ed8' : '#fff', 
      padding: '15px 10px', 
      borderRadius: '10px', 
      textAlign: 'center', 
      fontWeight: 800, 
      fontSize: '0.9rem', 
      position: 'relative', 
      overflow: 'hidden',
      border: `1px solid ${tipo === 'HB' ? '#fbcfe8' : tipo === 'HS' ? '#dbeafe' : 'transparent'}`
    }),
    turnoCell: { background: '#64748b', color: '#fff', padding: '8px', borderRadius: '8px', textAlign: 'center', fontWeight: 600, fontSize: '0.75rem' },
    slotCell: (status) => ({
      background: status === 'aberto' ? '#ff4757' : status === 'pendente' ? '#ffa502' : status === 'coringa' ? '#1e90ff' : status === 'liberacao_ativa' ? '#9b59b6' : status === 'liberacao_vencida' ? '#6d28d9' : '#2ed573',
      color: '#fff', padding: '10px 4px', borderRadius: '8px', textAlign: 'center', fontWeight: 800, fontSize: '0.85rem', cursor: 'pointer',
      height: '45px', verticalAlign: 'middle', transition: 'all 0.2s', border: 'none', width: '100%', boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      textShadow: '0 1px 3px rgba(0,0,0,0.2)',
      position: 'relative'
    }),
    checkIcon: { position: 'absolute', top: '2px', right: '2px', fontSize: '0.65rem', background: '#fff', borderRadius: '50%', width: '14px', height: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' },
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modalContent: { background: '#fff', padding: '32px', borderRadius: '24px', width: '450px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)' },
    input: { width: '100%', padding: '14px 18px', borderRadius: '12px', border: '2px solid #e2e8f0', marginTop: '10px', marginBottom: '20px', outline: 'none', boxSizing: 'border-box', fontSize: '1rem', transition: 'border-color 0.2s' },
    btn: { background: '#0f172a', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: '12px', cursor: 'pointer', fontWeight: 700, fontSize: '0.95rem', transition: 'all 0.2s', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h1 style={s.title}>📅 Gestão de Escalas</h1>
        <div style={s.controls}>
          <button style={{...s.btn, background: '#6366f1'}} onClick={toggleViewMode}>
            {viewMode === 'week' ? '📅 Ver Mês Inteiro' : '📅 Ver Semana'}
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <button style={s.btnIcon} onClick={() => changePeriod(-1)}>◀</button>
            <div style={{ fontWeight: 800, color: '#0f172a', background: '#fff', padding: '8px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', minWidth: '150px', textAlign: 'center' }}>
              {viewMode === 'week' ? `Semana: ${formatHeaderDate(displayDays[0].date)} - ${formatHeaderDate(displayDays[6].date)}` : displayDays[0].date.toLocaleString('pt-BR', { month: 'long', year: 'numeric' }).toUpperCase()}
            </div>
            <button style={s.btnIcon} onClick={() => changePeriod(1)}>▶</button>
          </div>
          <div style={{ position: 'relative' }}>
            <input 
              type="text" 
              placeholder="🔍 Buscar paciente..." 
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{
                padding: '10px 16px',
                paddingLeft: '40px',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                fontSize: '0.9rem',
                width: '200px',
                outline: 'none',
                boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
              }}
            />
            <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', opacity: 0.5 }}>🔍</span>
          </div>

          {(userRole === 'admin' || userRole === 'escalista' || isPainel) && listaCooperativas.length > 0 && (
            <select
              value={filterCooperativa}
              onChange={e => setFilterCooperativa(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '10px',
                border: '1px solid #e2e8f0',
                fontSize: '0.8rem',
                fontWeight: 700,
                color: filterCooperativa !== 'TODAS' ? '#4f46e5' : '#475569',
                background: filterCooperativa !== 'TODAS' ? '#eef2ff' : '#fff',
                cursor: 'pointer',
                outline: 'none',
                minWidth: '160px',
              }}
            >
              <option value="TODAS">Todas Cooperativas</option>
              {listaCooperativas.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          )}

          <div style={{ display: 'flex', gap: '4px', background: '#f1f5f9', padding: '4px', borderRadius: '10px' }}>
            <button
              onClick={() => setFilterFilial('TODOS')}
              style={{
                padding: '6px 12px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700,
                background: filterFilial === 'TODOS' ? '#475569' : 'transparent', color: filterFilial === 'TODOS' ? '#fff' : '#64748b'
              }}
            >TODOS</button>
            <button 
              onClick={() => setFilterFilial('HB')}
              style={{
                padding: '6px 12px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700,
                background: filterFilial === 'HB' ? '#be185d' : 'transparent', color: filterFilial === 'HB' ? '#fff' : '#be185d'
              }}
            >HOMEBABY</button>
            <button 
              onClick={() => setFilterFilial('HS')}
              style={{
                padding: '6px 12px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700,
                background: filterFilial === 'HS' ? '#1d4ed8' : 'transparent', color: filterFilial === 'HS' ? '#fff' : '#1d4ed8'
              }}
            >VITALCARE</button>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '20px', marginBottom: '20px' }}>
        {(userRole === 'admin' || userRole === 'escalista' || isPainel) && (
          <div style={{ ...s.tabs, marginBottom: 0 }}>
            <div style={s.tab(activeTab === 'vinculados')} onClick={() => setActiveTab('vinculados')}>
              🔗 Pacientes Vinculados
            </div>
            <div style={s.tab(activeTab === 'sem_vinculo')} onClick={() => setActiveTab('sem_vinculo')}>
              ❌ Pacientes Sem Vínculo
            </div>
          </div>
        )}

        <div style={{ ...s.legend, marginBottom: 0, flex: 1 }}>
          <div style={s.legendItem()}>Legendas:</div>
          <div style={s.legendItem()}><div style={s.legendDot('#ef4444')}></div> Turno em Aberto</div>
          <div style={s.legendItem()}><div style={s.legendDot('#f59e0b')}></div> Profissional Pendente (RH/TI)</div>
          <div style={s.legendItem()}><div style={s.legendDot('#10b981')}></div> Profissional Confirmado</div>
          <div style={s.legendItem()}><div style={s.legendDot('#3b82f6')}></div> Profissional Coringa 🃏</div>
          <div style={s.legendItem()}><div style={s.legendDot('#9b59b6')}></div> Lib. Temporária Ativa</div>
          <div style={s.legendItem()}><div style={s.legendDot('#6d28d9')}></div> Lib. Temp. VENCIDA</div>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Carregando...</div>
      ) : (
        <div style={s.gridContainer}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={{...s.th, width: '150px'}}>Paciente</th>
                <th style={{...s.th, width: '100px'}}>Turno</th>
                {displayDays.map((day, i) => (
                  <th key={i} style={{...s.th, padding: '8px 4px'}}>
                    <div style={{
                      background: 'rgba(255,255,255,0.15)', 
                      borderRadius: '6px', 
                      padding: '4px 0', 
                      marginBottom: '6px',
                      fontSize: '0.65rem'
                    }}>
                      <div style={{color: '#fff', fontWeight: 800}}>🔓 {dayTotals[i].open} ABERTOS</div>
                      <div style={{color: '#4ade80', fontWeight: 800}}>🔒 {dayTotals[i].closed} FECHADOS</div>
                    </div>
                    <div style={{fontWeight: 800}}>{viewMode === 'week' ? day.nome : day.date.getDate()}</div>
                    <div style={{fontSize:'0.65rem',opacity:0.9, marginTop: '2px'}}>
                      {viewMode === 'week' ? formatHeaderDate(day.date) : ''}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedPacientes.map(paciente => {
                const turnosDoPaciente = paciente.escala === '12h' ? [TURNOS[0]] : 
                                         paciente.escala === '12h_noite' ? [TURNOS[1]] : 
                                         TURNOS;
                return (
                  <React.Fragment key={paciente.id}>
                    {turnosDoPaciente.map((turno, index) => (
                      <tr key={turno}>
                        {index === 0 && (
                          <td rowSpan={turnosDoPaciente.length} style={s.pacienteCell(paciente.tipo)}>
                            <div style={{marginBottom: '5px'}}>{paciente.nome}</div>
                            {paciente.dataNascimento && (
                              <div style={{fontSize: '0.65rem', opacity: 0.7, marginBottom: '5px'}}>
                                🎂 {new Date(paciente.dataNascimento + 'T00:00:00').toLocaleDateString('pt-BR')}
                              </div>
                            )}
                            <div style={{display: 'flex', gap: '4px', justifyContent: 'center'}}>
                              <button 
                                onClick={()=>handleEditPaciente(paciente)} 
                                style={{
                                  background:'rgba(0,0,0,0.05)', 
                                  border:'1px solid rgba(0,0,0,0.1)', 
                                  color:'#3b82f6', 
                                  cursor:'pointer', 
                                  fontSize:'0.65rem',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontWeight: 600
                                }}
                              >
                                Editar
                              </button>
                              <button 
                                onClick={()=>handleDeletePaciente(paciente.id)} 
                                style={{
                                  background:'rgba(0,0,0,0.05)', 
                                  border:'1px solid rgba(0,0,0,0.1)', 
                                  color:'#dc2626', 
                                  cursor:'pointer', 
                                  fontSize:'0.65rem',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontWeight: 600
                                }}
                              >
                                Excluir
                              </button>
                            </div>
                          </td>
                        )}
                        <td style={s.turnoCell}>{turno}</td>
                        {displayDays.map((day, i) => {
                          const e = escalas.find(x => x.pacienteId === paciente.id && x.dataStr === day.dateStr && x.turno === turno);
                          
                          let status = 'aberto';
                          let label = 'EM ABERTO';
                          let isOtherCoop = false;
                          
                          let libStatusCell = null;
                          if (e && e.tecnico) {
                            isOtherCoop = userRole === 'cooperativa' && e.cooperativa && e.cooperativa !== userEquipe;

                            const profInfo = profissionaisAtivos.find(p => p.nome === e.tecnico);
                            libStatusCell = getLiberacaoStatus(profInfo);
                            if (libStatusCell) {
                              status = libStatusCell.ativa ? 'liberacao_ativa' : 'liberacao_vencida';
                            } else {
                              const isConfirmed = profInfo?.statusTI === 'Criado';
                              status = e.isCoringa ? 'coringa' : (isConfirmed ? 'fechado' : 'pendente');
                            }

                            if (isOtherCoop) {
                              label = 'FECHADO';
                              status = 'fechado';
                            } else {
                              label = e.tecnico.split(' ')[0] + (e.isCoringa ? ' 🃏' : '');
                            }
                          }

                          return (
                            <td key={i} style={s.slotCell(status)} onClick={() => handleOpenSlot(paciente, day, turno)}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center', justifyContent: 'center' }}>
                                <div style={{ fontSize: '0.85rem' }}>{label}</div>
                                {e && e.tecnico && !isOtherCoop && (
                                  <div style={{ fontSize: '0.55rem', opacity: 0.85, textTransform: 'uppercase', fontWeight: 600 }}>
                                    {e.cooperativa || 'Central'}
                                  </div>
                                )}
                                {libStatusCell && !isOtherCoop && (
                                  <div style={{ fontSize: '0.5rem', fontWeight: 800, background: 'rgba(0,0,0,0.2)', padding: '1px 4px', borderRadius: '3px' }}>
                                    {libStatusCell.ativa ? `⏰ ${libStatusCell.diasRestantes}d` : '⚠️ VENCIDA'}
                                  </div>
                                )}
                              </div>
                              {e && e.validadoEscalista && <div style={s.checkIcon}>✅</div>}
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
              {sortedPacientes.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Nenhum paciente cadastrado. Clique em "+ Adicionar Paciente".</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Implantados da Captação ── */}
      {(() => {
        const nomesEscalas = new Set(pacientes.map(p => (p.nome || '').toLowerCase().trim()));
        const pendentes = pacientesCapImplantados.filter(p => !nomesEscalas.has((p.nome || '').toLowerCase().trim()));
        if (pendentes.length === 0) return null;
        return (
          <div style={{ marginTop: 20, background: '#fff', borderRadius: 16, border: '1px solid #bfdbfe',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
            <div
              onClick={() => setShowCapImplantados(v => !v)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 20px', cursor: 'pointer', background: '#eff6ff',
                borderBottom: showCapImplantados ? '1px solid #bfdbfe' : 'none' }}
            >
              <span style={{ fontWeight: 800, color: '#1d4ed8', fontSize: '0.95rem' }}>
                🏥 Implantados da Captação não cadastrados nas Escalas ({pendentes.length})
              </span>
              <span style={{ color: '#1d4ed8', fontWeight: 700 }}>{showCapImplantados ? '▲' : '▼'}</span>
            </div>
            {showCapImplantados && (
              <div style={{ padding: '16px 20px', display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {pendentes.map(p => (
                  <div key={p.id} style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10,
                    padding: '10px 14px', minWidth: 220, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f172a' }}>{p.nome}</div>
                    <div style={{ fontSize: '0.75rem', color: '#475569' }}>
                      {[p.filial === 'Homesenior' ? 'Vitalcare' : p.filial, p.convenio].filter(Boolean).join(' · ')}
                    </div>
                    {(userRole === 'admin' || userRole === 'escalista') && (
                      <button
                        onClick={() => {
                          const tipo = (p.filial || '').toLowerCase().includes('baby') ? 'HB' : 'HS';
                          setPacienteForm({ nome: p.nome || '', dataNascimento: '', tipo, cooperativas: [], escala: '24h' });
                          setEditingPacienteId(null);
                          setIsPacienteModalOpen(true);
                        }}
                        style={{ marginTop: 6, padding: '4px 10px', borderRadius: 6, border: 'none',
                          background: '#0ea5e9', color: '#fff', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer' }}
                      >
                        + Adicionar às Escalas
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Modal Escala */}
      {isModalOpen && (
        <div style={s.modalOverlay}>
          <form style={s.modalContent} onSubmit={handleSaveSlot}>
            <h2 style={{ margin: '0 0 16px', fontSize: '1.2rem', color: '#0f172a' }}>Escalar Técnico</h2>
            <p style={{ margin: '0 0 16px', color: '#64748b', fontSize: '0.9rem' }}>
              <strong>Paciente:</strong> {selectedSlot.paciente.nome}<br/>
              <strong>Data:</strong> {selectedSlot.day.nome} ({formatHeaderDate(selectedSlot.day.date)}) <br/>
              <strong>Turno:</strong> {selectedSlot.turno}
            </p>
            
            <label style={{ fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>Profissional Alocado (Digite para buscar)</label>
            <input
              style={s.input}
              list="profissionais-list"
              placeholder="Digite o nome..."
              value={formTecnico}
              onChange={e => setFormTecnico(e.target.value)}
              onInput={e => setFormTecnico(e.target.value)}
              onBlur={e => setFormTecnico(e.target.value)}
              autoComplete="off"
            />
            {(() => {
              const selProf = profissionaisAtivos.find(p => p.nome === formTecnico || (p.cooperativa && `${p.nome} - ${p.cooperativa}` === formTecnico));
              const ls = getLiberacaoStatus(selProf);
              if (!ls) return null;
              return (
                <div style={{ padding: '10px 14px', borderRadius: '10px', marginBottom: '12px', fontSize: '0.82rem', fontWeight: 600,
                  background: ls.ativa ? '#f3e8ff' : '#fee2e2',
                  color: ls.ativa ? '#6b21a8' : '#991b1b',
                  border: `1px solid ${ls.ativa ? '#d8b4fe' : '#fca5a5'}` }}>
                  {ls.ativa
                    ? `⏰ Liberação temporária ativa — ${ls.diasRestantes} dia(s) restante(s) para regularizar`
                    : `⚠️ Liberação temporária VENCIDA há ${Math.abs(ls.diasRestantes)} dia(s). Bloqueado para datas futuras.`}
                </div>
              );
            })()}
            <datalist id="profissionais-list">
              {profissionaisAtivos
                .filter(p => {
                  if (userRole !== 'cooperativa') return true;
                  // Verificação robusta: UID ou Nome (ignorando caixa e espaços)
                  const isOwner = p.cooperativaUid === currentUser?.uid;
                  const myEquipe = (userEquipe || '').trim().toUpperCase();
                  const pCoop = (p.cooperativa || '').trim().toUpperCase();
                  return isOwner || (myEquipe && pCoop === myEquipe);
                })
                .map(p => (
                <option key={p.id} value={p.cooperativa ? `${p.nome} - ${p.cooperativa}` : p.nome}>
                  {p.nome} {p.isCoringa ? '(Coringa)' : `(${p.tipo})`} {p.cooperativa ? `- ${p.cooperativa}` : ''}
                </option>
              ))}
            </datalist>

            {(userRole === 'escalista' || userRole === 'admin' || isPainel) && (
              <div style={{marginTop: '16px', background: '#f8fafc', padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0'}}>
                <label style={{display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontWeight: 700, color: '#0f172a'}}>
                  <input 
                    type="checkbox" 
                    checked={isValidated} 
                    onChange={e => setIsValidated(e.target.checked)}
                    style={{width: '18px', height: '18px'}}
                  />
                  ✅ Validar presença (Escalista)
                </label>
              </div>
            )}

            {/* Diagnóstico visível */}
            <div style={{ background: '#f1f5f9', borderRadius: 8, padding: '8px 12px', marginTop: 12, fontSize: '0.75rem', color: '#475569' }}>
              <strong>Técnico digitado:</strong> {formTecnico || <em style={{color:'#ef4444'}}>VAZIO — digite um nome</em>}
            </div>

            {saveError && (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginTop: 10, fontSize: '0.8rem', color: '#991b1b', fontWeight: 600 }}>
                ⚠️ Erro: {saveError}
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button type="button" style={{...s.btn, background: '#f1f5f9', color: '#475569'}} onClick={() => setIsModalOpen(false)} disabled={saving}>Cancelar</button>
              <button type="submit" style={s.btn} disabled={saving}>{saving ? 'Salvando...' : 'Salvar Escala'}</button>
            </div>
          </form>
        </div>
      )}

      {/* Modal Adicionar/Editar Paciente */}
      {isPacienteModalOpen && (
        <div style={s.modalOverlay}>
          <form style={s.modalContent} onSubmit={handleSavePaciente}>
            <h2 style={{ margin: '0 0 16px', fontSize: '1.2rem', color: '#0f172a' }}>
              {editingPacienteId ? 'Editar Paciente' : 'Adicionar Novo Paciente'}
            </h2>
            
            <label style={{ fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>Nome Completo</label>
            <input 
              style={s.input} 
              placeholder="Nome do paciente..."
              value={pacienteForm.nome}
              onChange={e => setPacienteForm({...pacienteForm, nome: e.target.value})}
              required
            />

            <label style={{ fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>Data de Nascimento</label>
            <input 
              type="date"
              style={s.input} 
              value={pacienteForm.dataNascimento}
              onChange={e => setPacienteForm({...pacienteForm, dataNascimento: e.target.value})}
            />

            <label style={{ fontWeight: 600, fontSize: '0.9rem', color: '#334155', display: 'block', marginBottom: '8px' }}>Tipo de Atendimento</label>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
              <label style={{ 
                flex: 1, padding: '12px', borderRadius: '12px', border: '2px solid', 
                borderColor: pacienteForm.tipo === 'HB' ? '#fce4ec' : '#e2e8f0',
                background: pacienteForm.tipo === 'HB' ? '#fce4ec' : '#fff',
                cursor: 'pointer', textAlign: 'center', fontWeight: 700, fontSize: '0.9rem'
              }}>
                <input 
                  type="radio" name="tipo" value="HB" style={{ display: 'none' }}
                  checked={pacienteForm.tipo === 'HB'} 
                  onChange={() => setPacienteForm({...pacienteForm, tipo: 'HB'})}
                />
                Home Baby (HB)
              </label>
              <label style={{ 
                flex: 1, padding: '12px', borderRadius: '12px', border: '2px solid', 
                borderColor: pacienteForm.tipo === 'HS' ? '#e3f2fd' : '#e2e8f0',
                background: pacienteForm.tipo === 'HS' ? '#e3f2fd' : '#fff',
                cursor: 'pointer', textAlign: 'center', fontWeight: 700, fontSize: '0.9rem'
              }}>
                <input 
                  type="radio" name="tipo" value="HS" style={{ display: 'none' }}
                  checked={pacienteForm.tipo === 'HS'} 
                  onChange={() => setPacienteForm({...pacienteForm, tipo: 'HS'})}
                />
                Vitalcare (HS)
              </label>
            </div>

            <label style={{ fontWeight: 600, fontSize: '0.9rem', color: '#334155', display: 'block', marginBottom: '8px' }}>Escala de Atendimento</label>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
              <label style={{ 
                flex: 1, padding: '12px', borderRadius: '12px', border: '2px solid', 
                borderColor: pacienteForm.escala === '24h' ? '#dcfce7' : '#e2e8f0',
                background: pacienteForm.escala === '24h' ? '#dcfce7' : '#fff',
                cursor: 'pointer', textAlign: 'center', fontWeight: 700, fontSize: '0.9rem', color: pacienteForm.escala === '24h' ? '#166534' : '#64748b'
              }}>
                <input 
                  type="radio" name="escala" value="24h" style={{ display: 'none' }}
                  checked={pacienteForm.escala === '24h'} 
                  onChange={() => setPacienteForm({...pacienteForm, escala: '24h'})}
                />
                24 Horas (2 Turnos)
              </label>
              <label style={{ 
                flex: 1, padding: '12px', borderRadius: '12px', border: '2px solid', 
                borderColor: pacienteForm.escala === '12h' ? '#fef9c3' : '#e2e8f0',
                background: pacienteForm.escala === '12h' ? '#fef9c3' : '#fff',
                cursor: 'pointer', textAlign: 'center', fontWeight: 700, fontSize: '0.9rem', color: pacienteForm.escala === '12h' ? '#854d0e' : '#64748b'
              }}>
                <input 
                  type="radio" name="escala" value="12h" style={{ display: 'none' }}
                  checked={pacienteForm.escala === '12h'} 
                  onChange={() => setPacienteForm({...pacienteForm, escala: '12h'})}
                />
                12 Horas (Dia)
              </label>
              <label style={{ 
                flex: 1, padding: '12px', borderRadius: '12px', border: '2px solid', 
                borderColor: pacienteForm.escala === '12h_noite' ? '#e0e7ff' : '#e2e8f0',
                background: pacienteForm.escala === '12h_noite' ? '#e0e7ff' : '#fff',
                cursor: 'pointer', textAlign: 'center', fontWeight: 700, fontSize: '0.9rem', color: pacienteForm.escala === '12h_noite' ? '#3730a3' : '#64748b'
              }}>
                <input 
                  type="radio" name="escala" value="12h_noite" style={{ display: 'none' }}
                  checked={pacienteForm.escala === '12h_noite'} 
                  onChange={() => setPacienteForm({...pacienteForm, escala: '12h_noite'})}
                />
                12 Horas (Noite)
              </label>
            </div>

            <label style={{ fontWeight: 600, fontSize: '0.9rem', color: '#334155', display: 'block', marginBottom: '8px' }}>Cooperativas com acesso a este paciente:</label>
            <div style={{ 
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', 
              background: '#f8fafc', padding: '12px', borderRadius: '12px', 
              border: '1px solid #e2e8f0', maxHeight: '150px', overflowY: 'auto',
              marginBottom: '24px'
            }}>
              {listaCooperativas.map(coop => (
                <label key={coop} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.75rem', cursor: 'pointer', color: '#475569', fontWeight: 600 }}>
                  <input 
                    type="checkbox" 
                    checked={(pacienteForm.cooperativas || []).includes(coop)}
                    onChange={() => handleToggleCoopVinculo(coop)}
                    style={{ width: '16px', height: '16px' }}
                  />
                  {coop}
                </label>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button type="button" style={{...s.btn, background: '#f1f5f9', color: '#475569'}} onClick={() => { setIsPacienteModalOpen(false); setEditingPacienteId(null); }}>Cancelar</button>
              <button type="submit" style={s.btn}>
                {editingPacienteId ? 'Salvar Alterações' : 'Cadastrar Paciente'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
